import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress
} from '@mui/material';
import { CloseOutlined as CloseIcon } from '@mui/icons-material';
import { createFeedback } from '../../services/api';

const FeedbackModal = ({ open, onClose, onSuccess, onError }) => {
  const [feedbackType, setFeedbackType] = useState('');
  const [selectedFeature, setSelectedFeature] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const feedbackTypes = [
    'Request Support',
    'Submit Bug',
    'Feature Feedback',
    'Request Feature'
  ];

  const features = [
    'Capabilities',
    'Assistant',
    'Builder',
    'Execute & Creator',
    'Projects',
    'Knowledge',
    'Community',
    'Integrations & MCP',
    'User Management',
    'Account Settings',
    'Billing & Licenses',
    'Analytics & Usage',
    'Other'
  ];

  const showFeatureSelector = feedbackType === 'Submit Bug' || feedbackType === 'Feature Feedback';

  const handleFeedbackTypeChange = (event) => {
    const value = event.target.value;
    setFeedbackType(value);
    // Clear selected feature when switching away from bug/feature feedback
    if (value !== 'Submit Bug' && value !== 'Feature Feedback') {
      setSelectedFeature('');
    }
    // Clear errors when changing selection
    setErrors({});
  };

  const handleFeatureChange = (event) => {
    setSelectedFeature(event.target.value);
    setErrors(prev => ({ ...prev, selectedFeature: '' }));
  };

  const handleTextChange = (event) => {
    setFeedbackText(event.target.value);
    setErrors(prev => ({ ...prev, feedbackText: '' }));
  };

  const handleSubmit = async () => {
    const newErrors = {};
    
    if (!feedbackType) {
      newErrors.feedbackType = 'Please select a feedback type';
    }
    
    if (showFeatureSelector && !selectedFeature) {
      newErrors.selectedFeature = 'Please select a feature';
    }
    
    if (!feedbackText.trim()) {
      newErrors.feedbackText = 'Please provide your feedback';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      await createFeedback({
        feedbackType,
        selectedFeature: showFeatureSelector ? selectedFeature : null,
        feedbackText: feedbackText.trim()
      });

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Reset form and close on success
      handleClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      
      // Call error callback if provided
      if (onError) {
        onError(error?.message || 'Failed to submit feedback. Please try again.');
      } else {
        // Fallback to inline error display
        setErrors({ submit: error?.message || 'Failed to submit feedback. Please try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFeedbackType('');
    setSelectedFeature('');
    setFeedbackText('');
    setErrors({});
    setSubmitting(false);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Submit Feedback</Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {/* Feedback Type Selector */}
          <FormControl fullWidth error={!!errors.feedbackType}>
            <InputLabel>Feedback Type</InputLabel>
            <Select
              value={feedbackType}
              label="Feedback Type"
              onChange={handleFeedbackTypeChange}
            >
              {feedbackTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
            {errors.feedbackType && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.feedbackType}
              </Typography>
            )}
          </FormControl>

          {/* Feature Selector - only shown for Submit Bug or Feature Feedback */}
          {showFeatureSelector && (
            <FormControl fullWidth error={!!errors.selectedFeature}>
              <InputLabel>Feature</InputLabel>
              <Select
                value={selectedFeature}
                label="Feature"
                onChange={handleFeatureChange}
              >
                {features.map((feature) => (
                  <MenuItem key={feature} value={feature}>
                    {feature}
                  </MenuItem>
                ))}
              </Select>
              {errors.selectedFeature && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {errors.selectedFeature}
                </Typography>
              )}
            </FormControl>
          )}

          {/* Free-form text input - always visible */}
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Your Feedback"
            placeholder="Please describe your feedback, bug report, feature request, or support need..."
            value={feedbackText}
            onChange={handleTextChange}
            error={!!errors.feedbackText || !!errors.submit}
            helperText={errors.feedbackText || errors.submit || 'Provide as much detail as possible to help us assist you'}
            required
            disabled={submitting}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackModal;

